import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
	Box,
	Flex,
	Image,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Text,
	useBreakpointValue,
	useColorMode,
} from '@chakra-ui/react';
import { format, isSameDay, getDay } from 'date-fns';

import { ClockIcon } from '../icons';
import { EventDetailCard } from './';
import { Loader } from '../ui';
import { useCalendarSettings, useModal, useEventPositionContext, useFilters, styles } from '../../lib';

import type { IEvent } from '../../lib';

interface IEventProps {
	event: IEvent;
	continuesPrior?: boolean;
	continuesAfter?: boolean;
}

export const Event = ({ event, continuesPrior, continuesAfter }: IEventProps) => {
	const { colorMode } = useColorMode();
	const { openModal } = useModal();
	const { settings } = useCalendarSettings();
	const isDesktop = useBreakpointValue({ base: false, md: true });
	const { eventPositions } = useEventPositionContext();

	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [isEventHovered, setIsEventHovered] = useState(false);
	const [, setIsHoveringPopover] = useState(false);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const { currentView } = useFilters();
	const isSameDayEvent = useMemo(() => isSameDay(event.start, event.end), [event]);

	const handleOpenModal = useCallback(() => {
		openModal({ event, isSameDayEvent });
	}, [openModal, event, isSameDayEvent]);

	const handleContentClick = useCallback(() => {
		if (!settings?.showTooltip) {
			handleOpenModal();
		} else {
			setIsPopoverOpen((prev) => !prev);
		}
	}, [settings?.showTooltip, handleOpenModal]);

	const handleEventMouseEnter = useCallback(() => {
		setIsEventHovered(true);
		if (settings?.showTooltip) {
			if (!isPopoverOpen) setIsPopoverOpen(true);
		}
	}, [settings?.showTooltip, isPopoverOpen]);

	const handleEventMouseLeave = useCallback(() => {
		setIsEventHovered(false);
		if (isPopoverOpen) {
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHoveringPopover((hovering) => {
					if (!hovering) {
						setIsPopoverOpen(false);
					}
					return hovering;
				});
			}, 150); // small delay to trigger popover hover effect
		}
	}, [isPopoverOpen]);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	const handlePopoverClose = useCallback(() => {
		if (!isEventHovered) {
			setIsPopoverOpen(false);
			setIsHoveringPopover(false);
		}
	}, [isEventHovered]);

	const handlePopoverMouseEnter = useCallback(() => {
		setIsHoveringPopover(true);
	}, []);

	const handlePopoverMouseLeave = useCallback(() => {
		setIsHoveringPopover(false);
		setIsPopoverOpen(false);
	}, []);

	if (!event || !settings?.formats) return <Loader />;

	const { monthFormat, timeFormat } = settings.formats;
	const { showTooltip } = settings;

	const isSaturday = getDay(event.start) === 6;
	const ignoreDirection = isSaturday && !continuesPrior && continuesAfter;
	const { iconStyle, lineClampSx } = styles.event;

	const content = (
		<Box
			backgroundColor={
				event.color ||
				(settings?.eventCategoryColors &&
					event.eventType.length > 0 &&
					settings.eventCategoryColors[event.eventType[0]]?.background) ||
				settings.styles[colorMode]?.eventColor ||
				'teal.400'
			}
			borderRadius='lg'
			boxSizing='border-box'
			color='white'
			cursor='pointer'
			height='inherit'
			lineHeight={1.2}
			onClick={handleContentClick}
			onMouseEnter={handleEventMouseEnter}
			onMouseLeave={handleEventMouseLeave}
			whiteSpace='normal'
			marginTop={
				settings.enableFeaturedImage && eventPositions[event.id] && isDesktop
					? `-${eventPositions[event.id] - 2}px`
					: '0px'
			}
		>
			<Flex
				flexDirection={(isSameDayEvent && currentView !== 'agenda') || ignoreDirection ? 'column' : 'row'}
				alignItems='center'
				overflow='hidden'
			>
				{event.image && settings.enableFeaturedImage && !continuesPrior && (
					<Image margin='0px !important' src={event.image} maxW='150px' />
				)}
				<Box padding='10px'>
					<Text
						mb='0.5rem !important'
						fontSize='.9rem'
						fontWeight={600}
						sx={lineClampSx}
						color={event?.textColor || settings.styles[colorMode]?.eventTextColor || 'white'}
					>
						{event.title}
					</Text>
					<Text
						alignItems='baseline'
						color={event?.textColor || settings.styles[colorMode]?.eventTextColor || 'white'}
						display='flex'
						fontSize='.8rem'
						fontWeight={500}
						m={0}
					>
						<ClockIcon
							size={12}
							fill={event?.textColor || settings.styles[colorMode]?.eventTextColor || '#FFF'}
							style={iconStyle}
						/>
						{format(event.start, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)} -{' '}
						{format(event.end, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)}
					</Text>
				</Box>
			</Flex>
		</Box>
	);

	return showTooltip ? (
		<Popover placement='top-start' isLazy isOpen={isPopoverOpen} onClose={handlePopoverClose}>
			<PopoverTrigger>{content}</PopoverTrigger>
			<Portal>
				<PopoverContent
					w='350px'
					bg='none'
					boxShadow='none'
					onMouseEnter={handlePopoverMouseEnter}
					onMouseLeave={handlePopoverMouseLeave}
					className='cp-popover-content'
				>
					<PopoverBody p={0} bg='none' w='full'>
						<EventDetailCard event={event} isSameDayEvent={isSameDayEvent} />
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	) : (
		content
	);
};
